// --------------------------------------------------------------
// Created On: 2024-05-14
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-18
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import DashboardCard from "./DashboardCard/DashboardCard";
import {
  CREATE_ROLES_PERMISSION,
  UPDATE_ROLES_PERMISSION,
  DELETE_ROLES_PERMISSION,
  INVITE_USERS_PERMISSION,
  UPDATE_USERS_PERMISSION,
  DELETE_USERS_PERMISSION,
  CREATE_USER_GROUPS_PERMISSION,
  UPDATE_USER_GROUPS_PERMISSION,
  DELETE_USER_GROUPS_PERMISSION,
  PLACE_SALES_ORDERS_PERMISSION,
  PREPARE_QUOTES_PERMISSION,
} from "../../constants/permissions";
import Spinner from "../../components/Spinner/Spinner";
import userHasPermission from "../../utilities/userHasPermission";
import SearchBar from "../../components/SearchBar/SearchBar";
import DashboardCarousel from "./DashboardCarousel/DashboardCarousel";
import DashboardPromoCard from "./DashboardPromoCard/DashboardPromoCard";
import userHasUserPermissions from "../../utilities/userHasUserPermissions";
import userHasRolePermissions from "../../utilities/userHasRolePermissions";
import styles from "./DashboardGeneralPage.module.scss";

export default function DashboardGeneralPage(): Component {
  const [startSearch, setStartSearch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  return (
    <div className={`${styles.body} container-fluid pb-4 px-0`}>
      <Spinner loading={loading} />

      <DashboardCarousel />

      {/* User does not have any management permissions, render only a search bar. */}
      {!userHasPermission([
        [INVITE_USERS_PERMISSION],
        [UPDATE_USERS_PERMISSION],
        [DELETE_USERS_PERMISSION],
        [CREATE_USER_GROUPS_PERMISSION],
        [UPDATE_USER_GROUPS_PERMISSION],
        [DELETE_USER_GROUPS_PERMISSION],
        [CREATE_ROLES_PERMISSION],
        [UPDATE_ROLES_PERMISSION],
        [DELETE_ROLES_PERMISSION],
        [PLACE_SALES_ORDERS_PERMISSION],
        [PREPARE_QUOTES_PERMISSION],
      ]) ? (
        <div className={`${styles.dashboardRibbon} row px-2 justify-content-center`}>
          <div className="col-12 col-lg-8">
            <SearchBar
              isInNavbar={false}
              isInNavDrawer={false}
              isInDashboard={true}
              startSearch={startSearch}
              errorMessage={errorMessage}
              onSearchComplete={() => setStartSearch(false)}
              onErrorMessageChange={(errorMessage) => setErrorMessage(errorMessage)}
              onLoadingChange={(loading) => setLoading(loading)}
              onClose={() => {}}
            />
          </div>
        </div>
      ) : (
        <div className={`${styles.dashboardRibbon} row px-3 justify-content-center`}>
          <div className="col-12 col-lg-8">
            <div className="row">
              <DashboardCard icon="fa-shopping-cart" name="Start Order" url="" />
              <DashboardCard icon="fa-shopping-cart" name="Quick Order" url="/quick-order" />
              {userHasUserPermissions() && <DashboardCard icon="fa-users" name="Manage Users" url="/manage/users" />}
              {userHasRolePermissions() && <DashboardCard icon="fa-id-card" name="Manage Roles" url="/manage/roles" />}
              {userHasPermission([[PREPARE_QUOTES_PERMISSION]]) && (
                <DashboardCard icon="fa-solid fa-list" name="Manage Quotes" url="/sales-quotes" />
              )}
              <DashboardCard icon="fa-history" name="Order History" url="/order-history" />
            </div>
          </div>
        </div>
      )}

      <DashboardPromoCard />
    </div>
  );
}
